/**
 * Sincronizador único dos brindes automáticos do carrinho.
 *
 * Cada regra de brinde (header-brinde.js, bf-gifts.js...) registra um provider em
 * window.giftProviders e só DIZ quanto quer de cada brinde. Quem mexe no
 * carrinho é só este arquivo. Assim duas regras podem usar o mesmo produto de
 * brinde sem uma remover o que a outra adicionou (antes elas ficavam brigando).
 *
 * Provider: {
 *   name: string,
 *   giftIds(): number[]                 variantes que a regra controla
 *   expected(cartItems, ctx): Map<id, qty>  quantidade desejada de cada uma
 * }
 * ctx.subtotal = soma dos itens do carrinho que não são brinde (centavos).
 *
 * Quando mais de uma regra pede o mesmo brinde, vale a MAIOR quantidade
 * (o cliente não ganha o mesmo brinde em dobro por duas promoções).
 */
window.giftProviders = window.giftProviders || [];

function giftSyncTargets(cartItems) {
  const providers = window.giftProviders;
  const allGiftIds = new Set(providers.flatMap((provider) => provider.giftIds()));

  const subtotal = cartItems
    .filter((item) => !allGiftIds.has(item.variant_id))
    .reduce((total, item) => total + item.final_line_price, 0);

  // Todo brinde controlado começa zerado; cada regra só pode aumentar.
  const targets = new Map();
  allGiftIds.forEach((id) => targets.set(id, 0));

  for (const provider of providers) {
    try {
      const expected = provider.expected(cartItems, { subtotal });
      expected.forEach((quantity, id) => {
        targets.set(id, Math.max(targets.get(id) || 0, quantity));
      });
    } catch (e) {
      console.error(`[gift-sync] Erro na regra ${provider.name}:`, e);
    }
  }

  return targets;
}

async function giftSyncReconcile() {
  if (!window.giftProviders.length) return false;

  const cartItems = await fetch('/cart.js')
    .then((r) => r.json())
    .then((cart) => cart.items || []);

  const targets = giftSyncTargets(cartItems);
  let changed = false;

  for (const [variantId, targetQty] of targets) {
    const item = cartItems.find((i) => i.variant_id === variantId);
    const currentQty = item?.quantity || 0;
    if (currentQty === targetQty) continue;

    try {
      if (item) {
        // /cart/change.js exige o item key (variantId:hash), não o variant id
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: targetQty }),
        });
      } else {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: targetQty }] }),
        });
      }
      changed = true;
    } catch (e) {
      console.error('[gift-sync] Erro ao ajustar brinde:', e);
    }
  }

  return changed;
}

// Uma reconciliação por vez: eventos em sequência (add + re-render + outra regra)
// não podem ler o mesmo carrinho e adicionar o brinde duas vezes.
let giftSyncQueue = Promise.resolve(false);

function giftSync() {
  giftSyncQueue = giftSyncQueue
    .catch(() => false)
    .then(() => giftSyncReconcile());
  return giftSyncQueue;
}

subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
  // Ignora o próprio evento para não entrar em loop
  if (event?.source === 'gift-sync') return;

  const changed = await giftSync();
  // Aguarda o re-render do drawer: o cart.js segura o checkout travado até isto terminar.
  if (changed) await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-sync' });
});

// Cobre carrinho montado antes (refresh, navegação, promoção expirada)
document.addEventListener('DOMContentLoaded', async () => {
  try {
    const changed = await giftSync();
    if (changed) await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-sync' });
  } catch (e) {
    console.error('[gift-sync] Erro ao validar brindes no carregamento:', e);
  }
});
